import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import {
  AppAuthServiceService,
  CatalogService,
  SpinnerService,
  UserPreferencesService,
  BroadcastUtilsService,
} from '@core';
import { TranslateService } from '@ngx-translate/core';
import { CatalogItem, ShipmentIdentifier } from '@shared';
import { catchError, finalize, forkJoin, mergeMap, Observable, of } from 'rxjs';
import quickFadeIn from 'src/app/shared/animations/quick-fadeIn.animation';

@Component({
    selector: 'ups-user-preferences',
    templateUrl: './user-preferences.component.html',
    styleUrls: ['./user-preferences.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    animations: [quickFadeIn],
    standalone: false
})
export class UserPreferencesComponent implements OnInit {
  public userPreferencesForm: FormGroup = new FormGroup({
    language: new FormControl(null),
    inquirerCountry: new FormControl(null),
    inquirerCountryId: new FormControl(null),
    businessUnit: new FormControl(null),
    shipmentIdentifier: new FormControl(null),
  });

  public showTopToast = false;
  public languages: CatalogItem[] = [];
  public countries: CatalogItem[] = [];
  public businessUnits: CatalogItem[] = [];
  public shipmentIdentifiers: ShipmentIdentifier[] = [];
  public ready: boolean = false;
  private accountId: string;
  private setLangFail = false;

  constructor(
    private appAuthService: AppAuthServiceService,
    private readonly spinnerService: SpinnerService,
    private catalogService: CatalogService,
    private translateService: TranslateService,
    private userPreferences: UserPreferencesService,
    private broadcastUtilsService: BroadcastUtilsService,
    private router: Router
  ) {
    this.accountId = this.appAuthService.account.homeAccountId || '';
  }

  ngOnInit(): void {
    this.userPreferences.getUserPreferences(this.accountId).subscribe((preferences) => {
      if (preferences) {
        const { language, inquirerCountry, businessUnit, shipmentIdentifier, inquirerCountryId } = preferences;
        this.catalogService.setLanguage(preferences.language);
        this.userPreferencesForm.patchValue({
          language,
          inquirerCountry,
          businessUnit,
          shipmentIdentifier,
          inquirerCountryId,
        });
      }
      this.getFormValues([
        this.catalogService.userLanguage(),
        this.catalogService.inquiryCountry(),
        this.catalogService.shipmentIdentifier(),
      ]);
    });
  }

  public reloadBusinessUnits(clear = true): void {
    const countryName = this.userPreferencesForm.controls['inquirerCountry'].value;
    const country = this.countries.find((country) => country.apiName === countryName);
    if (country) {
      if (clear) {
        this.userPreferencesForm.patchValue({
          inquirerCountryId: country.id,
          businessUnit: null,
        });
      }
      this.spinnerService.show();
      this.catalogService
        .businessUnitByCountry(country.id)
        .pipe(finalize(() => this.spinnerService.hide()))
        .subscribe((businessUnits: any) => {
          this.businessUnits = businessUnits;
        });
    }
  }

  public processForm(): void {
    const formValues = this.userPreferencesForm.value;

    const { language } = this.userPreferencesForm.value;
    if (!language) {
      formValues.language = 'en';
    }

    this.userPreferences.update(this.accountId, formValues).subscribe(() => {
      this.translateService
        .use(language)
        .pipe(
          catchError((error) => of(error)),
          mergeMap((langResponse: any) => {
            if (langResponse.error && langResponse.status === 404) {
              this.setLangFail = true;
              formValues.language = 'en';
              // fallback - save 'en' in preferences if the user selects a none-available language
              return this.userPreferences.update(this.accountId, formValues);
            }
            return of(null);
          })
        )
        .subscribe(() => {
          this.catalogService.setLanguage(formValues.language);
          this.broadcastUtilsService.refreshBroadcastListener();
          this.router.navigate(['/home']);
        });
    });
  }

  private getFormValues(requests: Observable<any>[]): void {
    this.spinnerService.show();
    forkJoin(requests)
      .pipe(
        finalize(() => {
          this.spinnerService.hide();
          this.reloadBusinessUnits(false);
          this.ready = true;
        })
      )
      .subscribe((response) => {
        this.initValues(response);
      });
  }

  private initValues([languages, countries, shipmentIdentifiers]: any[]): void {
    this.languages = languages;
    this.countries = countries;
    this.shipmentIdentifiers = shipmentIdentifiers;
  }
}
